/**
 * Dockable-tiling layout model for the stage. Sources (video, map, and future
 * ones like an RC screen recording) are arranged in a recursive split tree; each
 * source can also be popped out to a floating PiP. This module is pure and
 * dependency-light (the render layer positions the actual, kept-mounted source
 * surfaces from the rectangles computed here) so the tree ops are unit-tested.
 */

export type SourceId = string; // 'video' | 'map' | future ('rec', 'cam', …)

export type TileNode =
  | { kind: 'leaf'; source: SourceId }
  | { kind: 'split'; dir: 'row' | 'col'; sizes: number[]; children: TileNode[] };

export interface FloatPane {
  source: SourceId;
  x: number; // fraction of stage
  y: number;
  w: number;
  h?: number; // fraction of stage; absent → legacy 16:9-derived height (back-compat)
}

export interface StageLayout {
  tree: TileNode | null;
  floats: FloatPane[];
}

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}
export interface LeafBox {
  source: SourceId;
  rect: Rect;
}
export interface DividerBox {
  path: number[]; // path to the split node
  index: number; // boundary is between children index-1 and index
  dir: 'row' | 'col';
  x: number; // centre of the divider strip (fractions)
  y: number;
  /** The split node's own rect — lets the UI convert a pixel drag to a fraction. */
  extent: Rect;
}

export type DropSide = 'left' | 'right' | 'top' | 'bottom';

export const leaf = (source: SourceId): TileNode => ({ kind: 'leaf', source });

/** All source ids in the tree, in visual order. */
export function sources(node: TileNode | null): SourceId[] {
  if (!node) return [];
  if (node.kind === 'leaf') return [node.source];
  return node.children.flatMap(sources);
}

export function hasSource(node: TileNode | null, s: SourceId): boolean {
  return sources(node).includes(s);
}

const normalize = (sizes: number[]): number[] => {
  const sum = sizes.reduce((a, b) => a + b, 0) || 1;
  return sizes.map((v) => v / sum);
};

/** Remove a source, collapsing splits that fall to a single child. */
export function removeSource(node: TileNode | null, s: SourceId): TileNode | null {
  if (!node) return null;
  if (node.kind === 'leaf') return node.source === s ? null : node;
  const kids: TileNode[] = [];
  const sz: number[] = [];
  node.children.forEach((c, i) => {
    const r = removeSource(c, s);
    if (r) {
      kids.push(r);
      sz.push(node.sizes[i]);
    }
  });
  if (kids.length === 0) return null;
  if (kids.length === 1) return kids[0];
  return { kind: 'split', dir: node.dir, sizes: normalize(sz), children: kids };
}

/** Replace the leaf holding `target` with a split that also holds `newSource`,
 *  placed on `side`. dir follows the side (left/right → row, top/bottom → col). */
export function splitSource(root: TileNode, target: SourceId, newSource: SourceId, side: DropSide): TileNode {
  const dir: 'row' | 'col' = side === 'left' || side === 'right' ? 'row' : 'col';
  const before = side === 'left' || side === 'top';
  const rec = (node: TileNode): TileNode => {
    if (node.kind === 'leaf') {
      if (node.source !== target) return node;
      const nn = leaf(newSource);
      return { kind: 'split', dir, sizes: [0.5, 0.5], children: before ? [nn, node] : [node, nn] };
    }
    return { ...node, children: node.children.map(rec) };
  };
  return rec(root);
}

/** Add a source to the layout (right-hand column) if not already tiled. */
export function addSource(layout: StageLayout, s: SourceId): StageLayout {
  if (hasSource(layout.tree, s)) return layout;
  if (!layout.tree) return { ...layout, tree: leaf(s) };
  return { ...layout, tree: { kind: 'split', dir: 'row', sizes: [0.62, 0.38], children: [layout.tree, leaf(s)] } };
}

/**
 * Re-dock `source` onto `target`'s `side` — the pane drag-drop operation. Pulls
 * `source` out of its current spot (and its floats), then re-splits it into
 * `target` on the requested edge. Kept as a pure op here (not inline in the UI)
 * so the drag-to-dock feature stays covered by tests even if its trigger chrome
 * changes — see test/tiling.test.mjs.
 */
export function dockOnto(stage: StageLayout, source: SourceId, target: SourceId, side: DropSide): StageLayout {
  if (source === target) return stage;
  const base = removeSource(stage.tree, source);
  const floats = stage.floats.filter((f) => f.source !== source);
  const tree = base ? splitSource(base, target, source, side) : leaf(source);
  return { tree, floats };
}

/** Where a source sat relative to a neighbour — captured on hide so a reopened
 *  pane returns to its old spot instead of always docking to the right. */
export interface DockHint {
  neighbor: SourceId;
  side: DropSide;
}

/** Capture `s`'s position as a (neighbour, side) pair: the sibling it was
 *  adjacent to and which edge of that sibling it sat on. Null if `s` isn't in
 *  the tree or has no sibling (it was the only pane). Restore with dockOnto. */
export function dockHint(node: TileNode | null, s: SourceId): DockHint | null {
  if (!node || node.kind === 'leaf') return null;
  const i = node.children.findIndex((c) => c.kind === 'leaf' && c.source === s);
  if (i >= 0) {
    const sibIdx = i > 0 ? i - 1 : i + 1;
    const sib = node.children[sibIdx];
    const neighbor = sib && sources(sib)[0];
    if (neighbor) {
      const before = i < sibIdx; // s sits before its sibling → left/top edge of it
      const side: DropSide = node.dir === 'row' ? (before ? 'left' : 'right') : before ? 'top' : 'bottom';
      return { neighbor, side };
    }
  }
  for (const c of node.children) {
    const h = dockHint(c, s);
    if (h) return h;
  }
  return null;
}

/** Drop every leaf whose source isn't in `avail`, collapsing as needed. */
export function pruneTo(node: TileNode | null, avail: Set<SourceId>): TileNode | null {
  if (!node) return null;
  if (node.kind === 'leaf') return avail.has(node.source) ? node : null;
  const kids: TileNode[] = [];
  const sz: number[] = [];
  node.children.forEach((c, i) => {
    const r = pruneTo(c, avail);
    if (r) {
      kids.push(r);
      sz.push(node.sizes[i]);
    }
  });
  if (kids.length === 0) return null;
  if (kids.length === 1) return kids[0];
  return { kind: 'split', dir: node.dir, sizes: normalize(sz), children: kids };
}

const FULL: Rect = { x: 0, y: 0, w: 1, h: 1 };

/** Compute the rectangle (stage fractions) for every leaf and the divider strips
 *  between siblings. */
export function computeLayout(root: TileNode | null, rect: Rect = FULL, path: number[] = []): { leaves: LeafBox[]; dividers: DividerBox[] } {
  if (!root) return { leaves: [], dividers: [] };
  if (root.kind === 'leaf') return { leaves: [{ source: root.source, rect }], dividers: [] };
  const leaves: LeafBox[] = [];
  const dividers: DividerBox[] = [];
  const sz = normalize(root.sizes);
  let off = 0;
  root.children.forEach((c, i) => {
    const frac = sz[i];
    const childRect: Rect =
      root.dir === 'row'
        ? { x: rect.x + off * rect.w, y: rect.y, w: frac * rect.w, h: rect.h }
        : { x: rect.x, y: rect.y + off * rect.h, w: rect.w, h: frac * rect.h };
    const sub = computeLayout(c, childRect, [...path, i]);
    leaves.push(...sub.leaves);
    dividers.push(...sub.dividers);
    if (i > 0) {
      dividers.push({
        path,
        index: i,
        dir: root.dir,
        x: root.dir === 'row' ? rect.x + off * rect.w : rect.x + rect.w / 2,
        y: root.dir === 'col' ? rect.y + off * rect.h : rect.y + rect.h / 2,
        extent: rect,
      });
    }
    off += frac;
  });
  return { leaves, dividers };
}

const cloneNode = (n: TileNode): TileNode => (n.kind === 'leaf' ? { ...n } : { ...n, sizes: [...n.sizes], children: n.children.map(cloneNode) });

export function nodeAt(root: TileNode, path: number[]): TileNode | null {
  let n: TileNode = root;
  for (const i of path) {
    if (n.kind !== 'split' || !n.children[i]) return null;
    n = n.children[i];
  }
  return n;
}

const MIN_FRAC = 0.08;

/** Move the boundary between children index-1 and index of the split at `path`
 *  by `deltaFrac` (fraction of that split's own extent). Returns a new tree. */
export function resizeSplit(root: TileNode, path: number[], index: number, deltaFrac: number): TileNode {
  const next = cloneNode(root);
  const node = nodeAt(next, path);
  if (!node || node.kind !== 'split') return root;
  const sizes = normalize(node.sizes);
  const a = index - 1;
  const b = index;
  let d = deltaFrac;
  d = Math.max(d, MIN_FRAC - sizes[a]);
  d = Math.min(d, sizes[b] - MIN_FRAC);
  sizes[a] += d;
  sizes[b] -= d;
  node.sizes = sizes;
  return next;
}

/** Migrate the legacy video/split/map modes into a tiling layout so existing
 *  users keep their arrangement on first load. */
export function fromModes(mainView: string, splitFrac: number, splitVideoLeft: boolean, hasVideo: boolean, hasMap: boolean): StageLayout {
  const srcs: SourceId[] = [];
  if (hasVideo) srcs.push('video');
  if (hasMap) srcs.push('map');
  if (srcs.length === 0) return { tree: null, floats: [] };
  if (srcs.length === 1) return { tree: leaf(srcs[0]), floats: [] };
  if (mainView === 'split') {
    const kids = splitVideoLeft ? [leaf('video'), leaf('map')] : [leaf('map'), leaf('video')];
    const sizes = splitVideoLeft ? [splitFrac, 1 - splitFrac] : [1 - splitFrac, splitFrac];
    return { tree: { kind: 'split', dir: 'row', sizes, children: kids }, floats: [] };
  }
  // video/map main → that source full, the other as a floating PiP.
  const main: SourceId = mainView === 'map' ? 'map' : 'video';
  const other: SourceId = main === 'video' ? 'map' : 'video';
  return { tree: leaf(main), floats: [{ source: other, x: 0.78, y: 0.72, w: 0.2 }] };
}
